import {
	addSelected,
	changeFilterSelect,
	changePage,
	changeRiskConfigPublishState,
	changeSearch,
	changeSlice,
	changeSort,
	clearRiskConfig,
	clearSelected,
	renameRiskCount,
	createRiskConfig,
	currentRow,
	deleteRisk,
	duplicateRiskConfig,
	getLog,
	getLogRejected,
	getLogResolved,
	getRiskConfig,
	getRiskConfigRejected,
	getRiskConfigResolved,
	getRiskCountResolved,
	getRiskDetails,
	getRiskDetailsRejected,
	getRiskDetailsResolved,
	removeSelected,
	renameRiskConfigAction,
	saveRiskConfigAction,
	setRefreshList,
	setRiskCalcStatus,
	setRiskConfigIsCreating,
	setRiskConfigNameError,
	checkRiskConfigName,
	setRiskConfigNameCheck,
	setRiskCountIsLoading,
	setRiskDetailsIsBusy,
	setRiskIsDeleting,
	setRiskIsSaving,
	setRiskSetupDirty,
	setRunCompleteReload,
	setSaveRun,
	setSelected,
	setTab,
	translate,
	updateColumnFilterSelectOptions,
	setUpdatedRanges,
	getSimulationData,
	getSimulationDataResolved,
	setDidMatrixUpdate,
	setUpdatedScores,
	setRiskType,
	setIsPreviewLoading,
	// setRiskMatrixPreviewRanges,
	setRiskMatrixAfterPreview,
	setMatrixPreviewStatus,
	setRefreshMatrixGrid,
	getTaskData,
	getTaskDataResolved,
	setIsPreviewOnLoad,
	setEditedCellsList,
	setEnablePreview,
	setOnlyGradesUpdated,
	clearMatrixUpdate,
	setOverallRisk,
	resetRiskResolved,
	setCancellPreview,
	changePagination,
	clearRiskScanId,
	changeFiltersSelect,
	setSearchStart,
	setSearchStartSaga,
	getRiskDetailsWSReseived,
	applyRiskTemplate,
	setTemplateApplyStatus,
	setTemplateApplyResult,
	getRiskConfigCoverage,
	getRiskConfigCoverageResolved,
	getRiskConfigCoverageRejected,
	getRiskAssetCount,
	getRiskAssetCountResolved,
	getRiskAssetCountRejected,
	getRiskResultsSummary,
	getRiskResultsSummaryResolved,
	getRiskResultsSummaryRejected,
	getRiskBreakdown,
	getRiskBreakdownResolved,
	getRiskBreakdownRejected,
	recordRunCompleted,
} from '@Actions';
import { createReducer } from '@reduxjs/toolkit';
import {
	ComponentCountReply,
	LogReply,
	RiskConfig,
	RiskMatrixGridObj,
	RiskStoreState,
} from '@Types';
import { AnyAction } from 'redux';
import * as AM_COMMON from '@innovyze/lib-am-common/Reducers';
import { paginationInitialState } from '@innovyze/lib-am-common/Reducers';
import { riskDetailsInitialState } from './filterInitialState';
import { ConfigPublishState } from '@Types/riskConfig.types';
import { InspectionStandardType } from '@Types/InspectionStandardType';
import { defaultRiskMatrix } from './RiskMatrixDefaultScores';
import { Pagination } from '@innovyze/lib-am-common';
import { templateApplyInitialState } from '@Types/riskTemplateApply.types';
import { CoverageEntry } from '@Types/riskCoverage.types';
import { resultsSummaryInitialState } from '@Types/riskResultsSummary.types';
import {
	riskBreakdownInitialState,
	riskBreakdownKey,
} from '@Types/riskBreakdown.types';

export interface CoverageState {
	status: 'idle' | 'loading' | 'loaded' | 'error';
	entries: CoverageEntry[];
}

export const coverageInitialState: CoverageState = {
	status: 'idle',
	entries: [],
};

export interface AssetCountState {
	status: 'idle' | 'loading' | 'loaded' | 'error';
	assetCount: number | null;
}

export const assetCountInitialState: AssetCountState = {
	status: 'idle',
	assetCount: null,
};

const configInitState: RiskConfig = {
	riskSetting: {
		publishState: ConfigPublishState.unpublished,
		inspectionStandard: InspectionStandardType.PACP,
		RiskMethod: 'multiplied',
		LOFWeighting: 50,
		COFWeighting: 50,
		Grades: {
			Low: 5,
			Medium: 10,
			High: 15,
			Extreme: 20,
			Max: 25,
		},
		LastRun: '',
		lastRunCompleted: '',
		configId: '',
		assetType: '',
		name: '',
		riskType: 'linear',
		matrix: { ...defaultRiskMatrix },
	},
	LOFCategories: [],
	COFCategories: [],
};

export const initState: RiskStoreState = {
	riskConfig: configInitState,
	isLoading: false,
	tab: '',
	riskDetails: paginationInitialState,
	search: '',
	startSearch: 0,
	saveRun: false,
	dropdownFilters: riskDetailsInitialState(),
	isSetupDirty: false,
	runCompleteReload: false,
	riskConfigNameError: '',
	riskConfigNameCheck: 'idle',
	isCreating: false,
	isSaving: false,
	isDeleting: false,
	refreshList: false,
	isCountLoading: [],
	riskCount: {},
	simulationData: [],
	simulationDataLoaded: false,
	taskData: [],
	taskDataLoaded: false,
	didMatrixUpdate: false,
	riskMatrix: { ...defaultRiskMatrix },
	enablePreview: false,
	cancelPreview: false,
	isPreviewLoading: false,
	previewStatus: '',
	previewObj: { grades: { lofRanges: [], cofRanges: [] }, grid: [] },
	refreshMatrixGrid: false,
	isPreviewOnLoad: false,
	editedCellsList: [],
	riskType: 'linear',
	isOnlyGradesUpdated: false,
	overallRisk: {
		riskMethod: configInitState.riskSetting.RiskMethod,
		lofWeighting: configInitState.riskSetting.LOFWeighting,
		cofWeighting: configInitState.riskSetting.COFWeighting,
	},
	templateApply: templateApplyInitialState,
	coverage: coverageInitialState,
	assetCount: assetCountInitialState,
	resultsSummary: resultsSummaryInitialState,
	riskBreakdown: riskBreakdownInitialState,
	riskBreakdownStale: false,
};

interface RiskReducer {
	[x: string]: (
		state: RiskStoreState,
		action: AnyAction,
	) => RiskStoreState | undefined;
}

export interface RiskCountChange {
	key: string;
	values?: ComponentCountReply[];
}

export interface RiskCountRename {
	existingKey: string;
	newKey: string;
}

export interface IsCountLoadingChange {
	key: string;
	loading: boolean;
}

const updateRiskCount = (state: RiskStoreState, action: RiskCountChange) => {
	if (action.values || state.riskCount[action.key])
		state.riskCount[action.key] = action.values;

	return state;
};

const copyRiskCount = (state: RiskStoreState, action: RiskCountRename) => {
	if (state.riskCount[action.existingKey]) {
		state.riskCount[action.newKey] = state.riskCount[action.existingKey];
		state.riskCount[action.existingKey] = undefined;
	}
	return state;
};

const updateIsCountLoading = (
	state: RiskStoreState,
	action: IsCountLoadingChange,
) => {
	if (action.loading && state.isCountLoading.indexOf(action.key) === -1)
		state.isCountLoading.push(action.key);
	else if (!action.loading && state.isCountLoading.indexOf(action.key) !== -1)
		state.isCountLoading = state.isCountLoading.filter(
			value => value !== action.key,
		);
};

// After a run, explanations are cleared only once the grid rows have reloaded,
// so a panel's breakdown and the row it reconciles against come from the same run.
const withRowsLoaded = (state: RiskStoreState): RiskStoreState =>
	state.riskBreakdownStale
		? {
				...state,
				riskBreakdown: riskBreakdownInitialState,
				riskBreakdownStale: false,
		  }
		: state;

export const reducer: RiskReducer = {
	[getRiskConfig.toString()]: state => ({
		...state,
		isLoading: true,
	}),
	[getRiskConfigResolved.toString()]: (state, action) => {
		const tempMatrix =
			action.payload.riskSetting?.matrix?.grades &&
			action.payload.riskSetting?.matrix?.grid?.length > 0
				? {
						grades: action.payload.riskSetting?.matrix?.grades,
						grid: action.payload.riskSetting?.matrix?.grid,
				  }
				: { ...defaultRiskMatrix };
		return {
			...state,
			riskConfig: action.payload,
			riskMatrix: { ...tempMatrix },
			riskType: action.payload.riskSetting?.riskType ?? 'linear',
			dropdownFilters: riskDetailsInitialState(),
			previewStatus: '',
			isPreviewOnLoad: false,
			isPreviewLoading: false,
			isLoading: false,
			cofCfgCategories: action.payload.cofCfgCategories,
			lofCfgCategories: action.payload.lofCfgCategories,
		};
	},
	[getRiskConfigRejected.toString()]: state => ({
		...state,
		isLoading: false,
	}),
	[clearRiskConfig.toString()]: state => ({
		...state,
		riskConfig: configInitState,
		riskDetails: paginationInitialState,
		search: '',
		saveRun: false,
		dropdownFilters: riskDetailsInitialState(),
		riskCount: {},
		isSetupDirty: false,
		templateApply: templateApplyInitialState,
		coverage: coverageInitialState,
		assetCount: assetCountInitialState,
		resultsSummary: resultsSummaryInitialState,
		riskBreakdown: riskBreakdownInitialState,
		riskBreakdownStale: false,
	}),
	[applyRiskTemplate.toString()]: state => ({
		...state,
		isCreating: true,
		templateApply: {
			...state.templateApply,
			status: 'applying',
		},
	}),
	[setTemplateApplyStatus.toString()]: (state, action) => ({
		...state,
		templateApply: {
			...state.templateApply,
			status: action.payload,
		},
	}),
	[setTemplateApplyResult.toString()]: (state, action) => ({
		...state,
		templateApply: {
			...state.templateApply,
			status: 'applied',
			availability: action.payload.availability,
			unrecognisedValues: action.payload.unrecognisedValues,
			templateLabel: action.payload.templateLabel,
		},
	}),
	[getRiskConfigCoverage.toString()]: state => ({
		...state,
		coverage: { ...state.coverage, status: 'loading' },
	}),
	[getRiskConfigCoverageResolved.toString()]: (state, action) => ({
		...state,
		coverage: { status: 'loaded', entries: action.payload },
	}),
	[getRiskConfigCoverageRejected.toString()]: state => ({
		...state,
		coverage: { status: 'error', entries: [] },
	}),
	[getRiskAssetCount.toString()]: state => ({
		...state,
		assetCount: { ...state.assetCount, status: 'loading' },
	}),
	[getRiskAssetCountResolved.toString()]: (state, action) => ({
		...state,
		assetCount: { status: 'loaded', assetCount: action.payload },
	}),
	[getRiskAssetCountRejected.toString()]: state => ({
		...state,
		assetCount: { status: 'error', assetCount: null },
	}),
	[getRiskResultsSummary.toString()]: (state, action) => ({
		...state,
		resultsSummary: {
			...state.resultsSummary,
			status: 'loading',
			configId: action.payload,
			// Same model (refetch after a run): keep showing the old numbers
			// until the new ones land. Different model: never show the
			// previous model's numbers under this model's name.
			data:
				state.resultsSummary.configId === action.payload
					? state.resultsSummary.data
					: undefined,
		},
	}),
	[getRiskResultsSummaryResolved.toString()]: (state, action) =>
		action.payload.configId === state.resultsSummary.configId
			? {
					...state,
					resultsSummary: {
						...state.resultsSummary,
						status: 'loaded',
						data: action.payload.data,
					},
			  }
			: state,
	[getRiskResultsSummaryRejected.toString()]: (state, action) =>
		action.payload === state.resultsSummary.configId
			? {
					...state,
					resultsSummary: {
						...state.resultsSummary,
						status: 'error',
						data: undefined,
					},
			  }
			: state,
	[getRiskBreakdown.toString()]: (state, action) => ({
		...state,
		riskBreakdown: {
			...state.riskBreakdown,
			[riskBreakdownKey(action.payload.configId, action.payload.assetId)]:
				{
					status: 'loading',
					requestId: action.payload.requestId,
				},
		},
	}),
	// Only the request that owns the entry may fill it: a run that completed
	// mid-flight clears the map (and open panels refetch), and a retry starts
	// a new request, so any other response is stale and dropped.
	[getRiskBreakdownResolved.toString()]: (state, action) => {
		const key = riskBreakdownKey(
			action.payload.configId,
			action.payload.assetId,
		);
		const current = state.riskBreakdown[key];
		if (!current || current.requestId !== action.payload.requestId)
			return state;
		return {
			...state,
			riskBreakdown: {
				...state.riskBreakdown,
				[key]: {
					status: 'loaded',
					requestId: current.requestId,
					data: action.payload.data,
				},
			},
		};
	},
	[getRiskBreakdownRejected.toString()]: (state, action) => {
		const key = riskBreakdownKey(
			action.payload.configId,
			action.payload.assetId,
		);
		const current = state.riskBreakdown[key];
		if (!current || current.requestId !== action.payload.requestId)
			return state;
		return {
			...state,
			riskBreakdown: {
				...state.riskBreakdown,
				[key]: { status: 'error', requestId: current.requestId },
			},
		};
	},
	[createRiskConfig.toString()]: state => ({
		...state,
		isCreating: true,
	}),
	[duplicateRiskConfig.toString()]: state => ({
		...state,
		isCreating: true,
	}),
	[changeRiskConfigPublishState.toString()]: (state, action) => ({
		...state,
		riskConfig: {
			...state.riskConfig,
			riskSetting: {
				...state.riskConfig.riskSetting,
				publishState: action.payload.newPublishState,
			},
		},
	}),
	[renameRiskConfigAction.toString()]: (state, action) => ({
		...state,
		...action.payload,
		isSaving: true,
	}),
	[saveRiskConfigAction.toString()]: (state, action) => ({
		...state,
		riskConfig: { ...action.payload },
		editedCellsList: [],
		isSaving: true,
	}),
	[setTab.toString()]: (state, action) => ({
		...state,
		tab: action.payload,
	}),
	[getSimulationData.toString()]: state => ({
		...state,
		simulationDataLoaded: false,
	}),
	[getSimulationDataResolved.toString()]: (state, action) => ({
		...state,
		simulationData: action.payload,
		simulationDataLoaded: true,
	}),
	[getTaskData.toString()]: state => ({
		...state,
		taskDataLoaded: false,
	}),
	[getTaskDataResolved.toString()]: (state, action) => ({
		...state,
		taskData: action.payload,
		taskDataLoaded: true,
	}),
	[getRiskDetails.toString()]: (state, action) => ({
		...state,
		riskDetails: AM_COMMON.updateGet(state.riskDetails, action.payload),
	}),
	[getRiskDetailsWSReseived.toString()]: (state, action) => {
		const searchStr = state.search;
		if (searchStr == '') {
			return {
				...withRowsLoaded(state),
				riskDetails: {
					...AM_COMMON.updateResolved(
						state.riskDetails,
						action.payload.response,
					),
					isWaiting: false,
				},
				startSearch: false,
			};
		} else {
			if (searchStr == action.payload.searchStr) {
				return {
					...withRowsLoaded(state),
					riskDetails: {
						...AM_COMMON.updateResolved(
							state.riskDetails,
							action.payload.response,
						),
						isWaiting: false,
					},
					startSearch: false,
				};
			} else {
				return state;
			}
		}
	},
	[getRiskDetailsResolved.toString()]: (state, action) => ({
		...withRowsLoaded(state),
		riskDetails: AM_COMMON.updateResolved(
			state.riskDetails,
			action.payload,
		),
	}),
	[getRiskDetailsRejected.toString()]: (state, action) => ({
		...state,
		riskDetails: AM_COMMON.updateRejected(
			state.riskDetails,
			action.payload,
		),
	}),
	[clearRiskScanId.toString()]: state => ({
		...state,
		riskDetails: { ...state.riskDetails, scanId: '' },
	}),
	[changePage.toString()]: (state, action) => ({
		...state,
		riskDetails: AM_COMMON.updatePage(state.riskDetails, action.payload),
	}),
	[changeSlice.toString()]: (state, action) => ({
		...state,
		riskDetails: AM_COMMON.updateSlice(state.riskDetails, action.payload),
	}),
	[changePagination.toString()]: (state, action) => {
		const query: Pagination = AM_COMMON.makePaginationQuery({
			page: action.payload?.page,
			slice: action.payload?.slice,
			pages: state.riskDetails.pagination.pages,
			total: state.riskDetails.pagination.total,
			subtotal: state.riskDetails.pagination.subtotal,
			search: state.riskDetails.pagination.search,
			sortKey: state.riskDetails.pagination.sortKey,
			sortDescending: state.riskDetails.pagination.sortDescending,
		});
		const pagination = {
			...state.riskDetails.pagination,
			slice:
				action.payload?.slice ?? state.riskDetails?.pagination?.slice,
			page: action.payload?.page ?? state.riskDetails?.pagination?.page,
		};

		return {
			...state,
			riskDetails: {
				...state.riskDetails,
				isWaiting: true,
				status: AM_COMMON.statusLoading(),
				pagination,
				query,
			},
		};
	},
	[changeSearch.toString()]: (state, action) => ({
		...state,
		search: action.payload,
		startSearch: state.startSearch + 1,
		riskDetails: AM_COMMON.updateSearch(state.riskDetails, action.payload),
	}),
	[setSearchStart.toString()]: (state, action) => ({
		...state,
		startSearch: action.payload
			? state.startSearch + 1
			: state.startSearch > 0
			? state.startSearch - 1
			: 0,
	}),
	[setSearchStartSaga.toString()]: (state, action) => ({
		...state,
		startSearch: action.payload ? 1 : 0,
	}),
	[changeSort.toString()]: (state, action) => ({
		...state,
		riskDetails: AM_COMMON.updateSort(state.riskDetails, action.payload),
	}),
	[clearSelected.toString()]: state => ({
		...state,
		riskDetails: AM_COMMON.setSelected(state.riskDetails, []),
	}),
	[setSelected.toString()]: (state, action) => ({
		...state,
		riskDetails: AM_COMMON.setSelected(state.riskDetails, action.payload),
	}),
	[addSelected.toString()]: (state, action) => ({
		...state,
		riskDetails: AM_COMMON.addSelected(state.riskDetails, action.payload),
	}),
	[removeSelected.toString()]: (state, action) => ({
		...state,
		riskDetails: AM_COMMON.removeSelected(
			state.riskDetails,
			action.payload,
		),
	}),
	[setRiskDetailsIsBusy.toString()]: (state, action) => ({
		...state,
		riskDetails: AM_COMMON.updateIsBusy(state.riskDetails, action.payload),
	}),
	[currentRow.toString()]: (state, action) => ({
		...state,
		riskDetails: AM_COMMON.setCurrentDbId(
			state.riskDetails,
			action.payload,
		),
	}),
	[setSaveRun.toString()]: (state, action) => ({
		...state,
		saveRun: action.payload,
	}),
	[setRiskCalcStatus.toString()]: (state, action) => ({
		...state,
		calcStatus: action.payload,
	}),
	[getLog.toString()]: state => ({
		...state,
		log: undefined,
		isLoading: true,
	}),
	[getLogResolved.toString()]: (state, action) => {
		const log: LogReply = action.payload;
		if (log.startTime && log.startTime != '')
			log.startTime = new Date(Date.parse(log.startTime)).toISOString();
		if (log.endTime && log.endTime != '')
			log.endTime = new Date(Date.parse(log.endTime)).toISOString();
		return {
			...state,
			log: log,
			isLoading: false,
			riskConfig: {
				...state?.riskConfig,
				riskSetting: {
					...state?.riskConfig?.riskSetting,
					LastRun: log.startTime,
					lastRunCompleted:
						// Update the lastRunCompleted time only if we completed the calc otherwise just keep it
						log.calcStatus === 'risk-calc-complete'
							? log.startTime
							: state.riskConfig.riskSetting.lastRunCompleted,
				},
			},
		};
	},
	[getLogRejected.toString()]: state => ({
		...state,
		isLoading: false,
	}),
	[changeFilterSelect.toString()]: (state, action) => ({
		...state,
		dropdownFilters: AM_COMMON.updateChangeFilterSelect(
			state.dropdownFilters,
			action.payload,
		),
	}),
	[changeFiltersSelect.toString()]: (state, action) => {
		const columns = action.payload ?? [];

		let dropdownFilters = { ...state.dropdownFilters };
		dropdownFilters.filters = {}; // Cleanup the filters

		if (columns.length !== 0) {
			dropdownFilters = AM_COMMON.changeMultipleFiltersSelect(
				dropdownFilters,
				columns,
			);
		} else {
			dropdownFilters.query = '';
		}

		return {
			...state,
			dropdownFilters: dropdownFilters,
		};
	},
	[updateColumnFilterSelectOptions.toString()]: (state, action) => ({
		...state,
		dropsownFilters: AM_COMMON.updateColumnFilterSelectOptions(
			state.dropdownFilters,
			action.payload,
		),
	}),
	[translate.toString()]: (state, action) => ({
		...state,
		dropsownFilters: AM_COMMON.updateTranslations(
			state.dropdownFilters,
			action.payload,
		),
	}),
	[setRiskSetupDirty.toString()]: (state, action) => ({
		...state,
		isSetupDirty: action.payload,
		enablePreview: action.payload,
	}),
	[setRunCompleteReload.toString()]: (state, action) => ({
		...state,
		runCompleteReload: action.payload,
	}),
	[recordRunCompleted.toString()]: (state, action) => ({
		...state,
		// Not cleared yet: open panels would refetch the new run's breakdown
		// while the grid still shows the old run's LoF/CoF. Cleared on the next
		// rows load instead (withRowsLoaded).
		riskBreakdownStale: true,
		resultsSummary: {
			...state.resultsSummary,
			completedRunConfigId: action.payload,
			completedRunCount: state.resultsSummary.completedRunCount + 1,
		},
	}),
	[setRefreshList.toString()]: (state, action) => ({
		...state,
		refreshList: action.payload,
	}),
	[setRiskConfigNameError.toString()]: (state, action) => ({
		...state,
		riskConfigNameError: action.payload,
	}),
	[checkRiskConfigName.toString()]: state => ({
		...state,
		riskConfigNameCheck: 'checking',
	}),
	[setRiskConfigNameCheck.toString()]: (state, action) => ({
		...state,
		riskConfigNameCheck: action.payload,
	}),
	[setRiskConfigIsCreating.toString()]: (state, action) => ({
		...state,
		isCreating: action.payload,
	}),
	[setRiskIsSaving.toString()]: (state, action) => ({
		...state,
		isSaving: action.payload,
	}),
	[deleteRisk.toString()]: state => ({
		...state,
		isDeleting: true,
	}),
	[setRiskIsDeleting.toString()]: (state, action) => ({
		...state,
		isDeleting: action.payload,
	}),

	[setRiskCountIsLoading.toString()]: (state, action) =>
		updateIsCountLoading(state, action.payload),
	[getRiskCountResolved.toString()]: (state, action) =>
		updateRiskCount(state, action.payload),
	[renameRiskCount.toString()]: (state, action) =>
		copyRiskCount(state, action.payload),
	[setOverallRisk.toString()]: (state, action) => ({
		...state,
		overallRisk: action.payload,
	}),
	//===risk matrix actions====
	[setUpdatedRanges.toString()]: (state, action) => {
		return {
			...state,
			riskMatrix: {
				...state?.riskMatrix,
				grades: action.payload,
			},
		};
	},
	[setDidMatrixUpdate.toString()]: (state, action) => {
		return {
			...state,
			didMatrixUpdate: action.payload,
		};
	},
	[setUpdatedScores.toString()]: (state, action) => {
		return {
			...state,
			riskMatrix: {
				...state?.riskMatrix,
				grid: action.payload,
			},
		};
	},
	[setRiskType.toString()]: (state, action) => {
		return {
			...state,
			riskConfig: {
				...state?.riskConfig,
				riskSetting: {
					...state?.riskConfig?.riskSetting,
					riskType: action.payload,
				},
			},
			riskType: action.payload,
		};
	},
	[setEnablePreview.toString()]: (state, action) => {
		return {
			...state,
			enablePreview: action.payload,
		};
	},
	[setCancellPreview.toString()]: (state, action) => {
		return {
			...state,
			cancelPreview: action.payload,
		};
	},
	[setIsPreviewLoading.toString()]: (state, action) => {
		return {
			...state,
			isPreviewLoading: action.payload,
		};
	},
	[setIsPreviewOnLoad.toString()]: (state, action) => {
		return {
			...state,
			isPreviewOnLoad: action.payload,
		};
	},
	[setMatrixPreviewStatus.toString()]: (state, action) => {
		return {
			...state,
			previewStatus: action.payload,
		};
	},
	[setRiskMatrixAfterPreview.toString()]: (state, action) => {
		return {
			...state,
			riskMatrix: action.payload,
		};
	},
	[setRefreshMatrixGrid.toString()]: (state, action) => {
		return {
			...state,
			refreshMatrixGrid: action.payload,
		};
	},
	[setEditedCellsList.toString()]: (state, action) => {
		return {
			...state,
			editedCellsList: [...state.editedCellsList, action.payload],
		};
	},
	[setOnlyGradesUpdated.toString()]: (state, action) => {
		return {
			...state,
			isOnlyGradesUpdated: action.payload,
		};
	},
	[clearMatrixUpdate.toString()]: (state, action) => {
		let tempMatrix = { ...defaultRiskMatrix };

		if (
			state.riskConfig.riskSetting?.matrix?.grades &&
			state.riskConfig.riskSetting?.matrix?.grid?.length > 0
		) {
			let tempGrade = state.riskConfig.riskSetting?.matrix?.grades;
			if (action.payload) {
				tempGrade = defaultRiskMatrix.grades;
			}
			const tempGrid: RiskMatrixGridObj[] =
				state.riskConfig.riskSetting?.matrix?.grid.map(entry => {
					return {
						pipeCount: 0,
						pipeLength: 0,
						grade: entry.grade,
						position: entry.position,
					};
				});
			tempMatrix = {
				grades: tempGrade,
				grid: tempGrid,
			};
		}
		return {
			...state,
			riskMatrix: { ...tempMatrix },
			previewStatus: '',
			isPreviewOnLoad: false,
		};
	},
	[resetRiskResolved.toString()]: state => {
		return {
			...state,
			riskRunning: false,
		};
	},
	//==========
};

export const riskReducer = createReducer(initState, reducer);
